import { z } from 'zod';
import { CAMPOS_MUDANCA, SITUACOES_ATO, TIPOS_ATO } from './vocabulario.js';
import { sociedadeSchema } from './sociedade.js';
/**
 * Uma mudança atômica dentro de um ato.
 *
 * `de` e `para` guardam o recorte do estado que mudou, serializado. Guardar o
 * "de" é o que permite gerar a cláusula de alteração ("fica alterado o
 * endereço da sede, que passa a ser…") sem ninguém redigir nada.
 */
export const mudancaSchema = z.object({
    campo: z.enum(CAMPOS_MUDANCA),
    de: z.unknown(),
    para: z.unknown(),
});
/**
 * O ATO é a unidade rastreável deste sistema — o equivalente ao que a nota
 * fiscal é para o faturamento. O documento .docx é subproduto dele, e pode ser
 * regerado a qualquer momento a partir do mesmo ato.
 *
 * `estadoDepois` é o snapshot pós-fold, congelado no momento em que o ato é
 * gerado. Congelar é deliberado: o documento protocolado na Junta tem de
 * continuar reproduzível daqui a cinco anos, mesmo que a biblioteca de
 * cláusulas ou as regras de validação tenham mudado desde então.
 */
export const atoSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    sociedadeId: z.string().uuid(),
    tipo: z.enum(TIPOS_ATO),
    /**
     * Ordinal do ato na vida da sociedade: 1 é a constituição, 2 é a primeira
     * alteração, e assim por diante. O rótulo ("PRIMEIRA ALTERAÇÃO") é derivado
     * daqui — nunca digitado, que é como se produzem duas "primeiras alterações".
     */
    sequencia: z.number().int().positive(),
    data: z.string().date(),
    situacao: z.enum(SITUACOES_ATO),
    mudancas: z.array(mudancaSchema),
    /** Congelado na geração. Ver comentário acima. */
    estadoDepois: sociedadeSchema.nullable().default(null),
    /** Versão da biblioteca de cláusulas usada. Reprodutibilidade do documento. */
    versaoClausulas: z.string().nullable().default(null),
    /** Preenchido quando a Junta devolve exigência. Vira trabalho na fila. */
    exigencia: z.string().nullable().default(null),
});
const ORDINAIS = [
    'PRIMEIRA',
    'SEGUNDA',
    'TERCEIRA',
    'QUARTA',
    'QUINTA',
    'SEXTA',
    'SÉTIMA',
    'OITAVA',
    'NONA',
    'DÉCIMA',
];
/**
 * Rótulo do ato como ele aparece no cabeçalho do documento.
 *
 * A constituição é a sequência 1; a primeira alteração é a 2. Acima da décima,
 * a Junta aceita a forma numérica — e escrever "DÉCIMA PRIMEIRA" por extenso
 * à mão é justamente onde a numeração começa a divergir entre documentos.
 */
export function rotuloDoAto(tipo, sequencia, tipoSocietario) {
    /*
      A constituição de uma companhia produz ESTATUTO SOCIAL, não contrato — e a
      diferença não é de nome: são documentos de natureza distinta (ver
      `biblioteca/estatuto.ts`). Um estatuto com "CONTRATO SOCIAL" no cabeçalho é
      devolvido pela Junta antes de o resto ser lido.
    */
    if (tipo === 'CONSTITUICAO') {
        return tipoSocietario === 'SA' ? 'ESTATUTO SOCIAL' : 'CONTRATO SOCIAL';
    }
    /*
      Os atos que não são contrato têm cabeçalho próprio, e nenhum deles é
      numerado por ordinal: um distrato acontece uma vez, uma procuração é
      identificada pelo outorgado, e uma ata pela data da reunião. Numerá-los
      como "PRIMEIRA PROCURAÇÃO" seria inventar uma sequência que ninguém usa.
  
      Antes desta lista, `rotuloDoAto` devolvia o TIPO CRU — o documento sairia
      com "DISTRATO" no cabeçalho, em caixa alta e sem acento, como um enum
      vazado para o papel que vai à Junta.
    */
    const PROPRIO = {
        DISTRATO: 'DISTRATO SOCIAL',
        PROCURACAO: 'PROCURAÇÃO',
        ATA: 'ATA DE REUNIÃO DE SÓCIOS',
        TRANSFORMACAO: 'INSTRUMENTO DE TRANSFORMAÇÃO',
    };
    const proprio = PROPRIO[tipo];
    if (proprio)
        return proprio;
    if (tipo !== 'ALTERACAO')
        return tipo;
    const ordem = sequencia - 1;
    const porExtenso = ORDINAIS[ordem - 1];
    return porExtenso
        ? `${porExtenso} ALTERAÇÃO CONTRATUAL`
        : `${ordem}ª ALTERAÇÃO CONTRATUAL`;
}
//# sourceMappingURL=ato.js.map